import { useCallback, useEffect, useRef, useState } from 'react'

import { EVENT_TYPES, useSubscribeToEvent } from 'services/event-bus'
import { useTimelineContext } from 'context/TimelineContext'
import { ITimelineItem } from 'types/timeline'
import { isPlayheadOverItem } from 'utils/timeline'

type TSyncPoints = number[]
interface IUsePlayheadOverlappingItemsParams {
  isAnyItemBuffering: boolean
  timelineItems: ITimelineItem[]
}

export default ({
  timelineItems,
  isAnyItemBuffering
}: IUsePlayheadOverlappingItemsParams) => {
  const [overlappingItemIds, setOverlappingItemIds] = useState<string[]>([])
  const syncPointsTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const { isPlaying, getCurrentTimeImmediate } = useTimelineContext()
  const isPlaybackPlaying = isPlaying && !isAnyItemBuffering

  const getOverlappingItemsByTime = useCallback(
    (time: number) =>
      timelineItems.filter(item => {
        const { startTime, duration } = item
        return isPlayheadOverItem({
          playheadTime: time,
          itemStartTime: startTime,
          itemDuration: duration
        })
      }),
    [timelineItems]
  )

  const getOverlappingItemIdsByTime = useCallback(
    (time: number) => getOverlappingItemsByTime(time).map(item => item.id),
    [getOverlappingItemsByTime]
  )

  const getOverlappingSyncPoints = useCallback(
    (playbackCurrentTime: number) => {
      return timelineItems.reduce((acc, item) => {
        const { startTime, duration } = item
        const endTime = startTime + duration
        const startTimeSyncPoint = startTime - playbackCurrentTime
        const endTimeSyncPoint = endTime - playbackCurrentTime
        if (startTimeSyncPoint > 0 && !acc.includes(startTimeSyncPoint)) {
          acc.push(startTimeSyncPoint)
        }
        if (endTimeSyncPoint > 0 && !acc.includes(endTimeSyncPoint)) {
          acc.push(endTimeSyncPoint)
        }
        return acc
      }, [] as TSyncPoints)
    },
    [timelineItems]
  )

  const clearSyncTimers = useCallback(() => {
    syncPointsTimersRef.current.forEach(timer => {
      clearTimeout(timer)
    })
    syncPointsTimersRef.current = []
  }, [])

  const updateSyncTimers = useCallback(() => {
    clearSyncTimers()
    const playbackCurrentTime = getCurrentTimeImmediate()
    const syncPoints = getOverlappingSyncPoints(playbackCurrentTime)

    syncPoints.forEach((syncDelay: number) => {
      const timer = setTimeout(() => {
        const playbackSyncTime = playbackCurrentTime + syncDelay
        const overlappingItemIds = getOverlappingItemIdsByTime(playbackSyncTime)
        setOverlappingItemIds(overlappingItemIds)
      }, syncDelay * 1000)

      syncPointsTimersRef.current.push(timer)
    })
  }, [
    clearSyncTimers,
    getCurrentTimeImmediate,
    getOverlappingSyncPoints,
    getOverlappingItemIdsByTime
  ])

  // update overlapping items and sync timersif timeline items are changed
  useEffect(() => {
    const playbackCurrentTime = getCurrentTimeImmediate()
    const visibleMediaItemsIds =
      getOverlappingItemIdsByTime(playbackCurrentTime)
    setOverlappingItemIds(visibleMediaItemsIds)
    if (isPlaybackPlaying) {
      updateSyncTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineItems])

  // update sync timers if playback state is changed
  useEffect(() => {
    if (isPlaybackPlaying) {
      updateSyncTimers()
    } else {
      clearSyncTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaybackPlaying])

  const handlePlaybackTimeChange = useCallback(
    (time: number) => {
      const overlappingItemIds = getOverlappingItemIdsByTime(time)
      setOverlappingItemIds(overlappingItemIds)
      if (isPlaying) {
        updateSyncTimers()
      } else {
        clearSyncTimers()
      }
    },
    [getOverlappingItemIdsByTime, isPlaying, clearSyncTimers, updateSyncTimers]
  )

  useSubscribeToEvent(
    EVENT_TYPES.PLAYBACK_TIME_CHANGE,
    handlePlaybackTimeChange
  )

  return overlappingItemIds
}
